
import { Component, Vue, Prop } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import skill_routes from "@/api/routes/skills";
import program_routes from "@/api/routes/programs";
import structure_routes from "@/api/routes/structures";
import pillar_routes from "@/api/routes/pillars";
import users_routes from "@/api/routes/users";
import block_routes from "@/api/routes/blocks";

import CStructure, { IStructureProp } from "@/components/Structure.vue";
import CPaginatedSelect from "@/components/PaginatedSelect.vue";

import ProgramProgram, { IProgramAuthor } from "@/models/program/program";
import SkillLocal, { ESkillCategory } from "@/models/skill/local";
import SkillCategory from "@/models/skill/category";
import BlockBlock from "@/models/block/block";
import PillarPillar from "@/models/pillar/pillar";
import StructureFactory from "@/models/structure/factory";
import UserUser from "@/models/user/user";
import { EAdmissionPermission } from "@/models/admission/permission";

@Component({
  name: "CProgramSettings",
  components: {
    CStructure,
    CPaginatedSelect
  }
})
export default class CProgramSettings extends Vue {
  @Prop({ required: true }) private program_id!: string;
  @Prop({ required: true }) public program!: ProgramProgram;

  @Prop({ required: false, default: false }) public disabled!: boolean;

  @Prop({ required: false, default: true })
  public show_btn!: boolean;

  public factory_id: string | null = null;
  public factories_options: StructureFactory[] = [];

  public structure: IStructureProp = {};

  public category_id: number | null = ESkillCategory.FUNCTIONAL;
  public categories_options: SkillCategory[] = [];

  public block_id: string | null = null;
  public blocks_options: BlockBlock[] = [{ id: null, full_name: "Все" }];

  public pillar_id: string | null = null;
  public pillars_options: PillarPillar[] = [{ id: null, full_name: "Все" }];

  public available_authors: UserUser[] = [];
  public current_author: IProgramAuthor | null = null;

  public current_days_to_study: number = 30;
  public current_days_to_study_error: boolean = false;

  public program_edit_message: string = this.$store.state.programEditMessage;

  private reload_skills_fn: Function | null = null;
  private skill: SkillLocal | null = null;

  public preload: boolean = false;

  private update_params: {
    infinity_id?: string | null;
    skill_id?: string | null;
    is_self_education?: boolean;
  } = {};

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.preload = true;

    if (this.program.draft_version) {
      this.current_days_to_study = this.program.draft_version?.days_to_study;
    }

    if (this.program.authors?.length) {
      this.current_author = this.program.authors[0];
    }

    this.watchers.push(
      this.$store.watch(
        state => state.programEditMessage,
        programEditMessage => {
          this.program_edit_message = programEditMessage;
        }
      )
    );

    if (this.isAdminOrDeveloper()) {
      this.category_id = null;
      this.categories_options.push({ id: null, value: "Все" });
    }

    const promises = [
      this.loadPillars(),
      this.loadCategories(),
      this.loadBlocks(),
      this.loadFactory(),
      this.loadSkill()
    ];

    if (this.isAdminOrDeveloper()) {
      promises.push(this.loadAvailableAuthors());
    }

    await Promise.all(promises).finally(() => (this.preload = false));
  }

  private async loadPillars() {
    this.$api
      .get(pillar_routes.pillars, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: PillarPillar[] }) => {
        this.pillars_options.push(...res);
      });
  }

  private async loadCategories() {
    return this.$api
      .get(skill_routes.categories, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: SkillCategory[] }) => {
        if (this.isAdminOrDeveloper()) {
          this.categories_options.push(...res);
        } else {
          this.categories_options.push(
            ...res.filter(c => c.id === ESkillCategory.FUNCTIONAL)
          );
        }
      });
  }

  private async loadBlocks() {
    return this.$api
      .get(block_routes.blocks, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: { blocks: BlockBlock[] } }) => {
        this.blocks_options.push(...res.blocks);
      });
  }

  private async loadSkill() {
    if (!this.program.settings.skill_id) {
      return;
    }

    return this.$api
      .get(skill_routes.skill(this.program.settings.skill_id), {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: SkillLocal }) => {
        this.skill = res;

        this.changeProgramSkillCategoryId();
      });
  }

  private async loadFactory() {
    return this.$api
      .get(structure_routes.factory(this.program.settings.factory_id), {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: StructureFactory }) => {
        this.factories_options.push(res);
        this.factory_id = res.id;

        this.structure = {
          factory_id: res.id,
          infinity_id: this.program.settings.infinity_id,
          parent_infinity_ids: this.program.settings.parent_infinity_ids,
          workshop_id: null,
          workline_id: null,
          equipment_id: null
        };
      });
  }

  private async loadAvailableAuthors() {
    return this.$api
      .get(users_routes.users, {
        params: {
          paginate: false,
          only_my: false,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0],
          permission_id: EAdmissionPermission.EDUCATION_PROGRAMS
        }
      })
      .then(({ data: res }: { data: { users: UserUser[] } }) => {
        this.available_authors = res.users;
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  public async changeCategory() {
    if (this.reload_skills_fn) {
      this.reload_skills_fn.call(null);
    }
  }

  public async changeBlock() {
    if (this.reload_skills_fn) {
      this.reload_skills_fn.call(null);
    }
  }

  public async changePillar() {
    if (this.reload_skills_fn) {
      this.reload_skills_fn.call(null);
    }
  }

  public setReloadSkills(reload_fn: Function) {
    this.reload_skills_fn = reload_fn;
  }

  public async loadSkillFn(page: number = 0, search_query: string) {
    return this.$api
      .get(skill_routes.skills, {
        params: {
          includes: "all",
          executed_skill_in_program: true,
          current_skill: this.program.settings.skill_id,
          only_is_local: false,
          responsible: false,

          block_id: this.block_id,
          pillar_id: this.pillar_id,
          category_id: this.category_id,

          exclude_archived: true,

          factory_id: this.factory_id,
          infinity_id: this.structure.infinity_id,

          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0],

          page,
          per_page: 50,
          paginate: true,
          search: search_query
        }
      })
      .then(({ data: res }: { data: { skills: SkillLocal[] } }) => {
        if (res.skills.length) {
          return res.skills;
        }

        return [];
      });
  }

  public async changeSkill(skill: SkillLocal | null) {
    if (!skill) {
      return;
    }

    this.skill = skill;

    this.program.settings.skill_id = skill.id;

    this.update_params.skill_id = skill.id;
    this.update_params.infinity_id = skill.infinity_id ?? null;

    await this.updateProgram();

    this.changeProgramSkillCategoryId();
  }

  public skillTooltip() {
    let tooltip = "Выберите навык";

    if (this.skill) {
      tooltip = this.skill.full_name;

      if (this.skill.workplace_full_name != null) {
        tooltip += ` | ${this.skill.workplace_full_name}`;
      }
    }

    return tooltip;
  }

  public async changeStructure(structure: IStructureProp) {
    this.structure = structure;
  }

  public async changeEducation() {
    this.update_params.is_self_education =
      this.program.settings.is_self_education ?? false;

    await this.updateProgram();
  }

  public async changeDaysToStudy() {
    if (!this.program.draft_version?.id) {
      return;
    }

    if (this.current_days_to_study < 1) {
      this.current_days_to_study_error = true;

      return this.$notify({
        group: "notifications",
        type: "error",
        text: "Количество дней должно быть больше нуля",
        duration: 500,
        speed: 500
      });
    }

    return this.$api
      .put(program_routes.version(this.program.draft_version.id), {
        days_to_study: this.current_days_to_study
      })
      .then(async () => {
        this.toggleProgramEditMessage();

        this.current_days_to_study_error = false;

        await this.changesExist();
      })
      .catch(({ response: res }) => {
        this.current_days_to_study_error = true;

        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }

  public async changeAuthor() {
    if (!this.current_author || this.current_author.id_type === "author_id") {
      if (this.program.authors?.length) {
        this.current_author = this.program.authors[0];
      }

      return;
    }

    return this.$api
      .put(program_routes.change_author(this.program_id), {
        user_id: this.current_author.id
      })
      .then(({ data: res }: { data: IProgramAuthor }) => {
        this.toggleProgramEditMessage();

        this.program.authors = [res];
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }

  private async updateProgram() {
    return this.$api
      .put(program_routes.edit(this.program_id), this.update_params)
      .then(async () => {
        this.toggleProgramEditMessage();

        await this.changesExist();
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }

  private changeProgramSkillCategoryId() {
    if (this.skill) {
      this.$store.commit("programSkillCategoryId", this.skill.category_id);
    }
  }

  private async changesExist() {
    if (!this.program.draft_version?.id) {
      return;
    }

    return this.$api
      .put(program_routes.changes_exist(this.program.draft_version.id), {})
      .then(() => {
        this.$store.commit("programChangeExist", true);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }

  private toggleProgramEditMessage() {
    this.$store.dispatch("toggleProgramEditMessage");
  }

  public showSettingsModal() {
    this.$modal.show("settings_program_modal");
  }

  public hideSettingsModal() {
    this.$modal.hide("settings_program_modal");
  }

  public isAdminOrDeveloper() {
    return this.$api.isAdminOrDeveloper();
  }
}
